/**
 * InviteTeamCard — dismissible dashboard card.
 *
 * Moved out of the 6 screen welcome intro (iter323): asking for colleagues'
 * emails before the user has a single project was the highest drop-off step.
 * It now appears on the dashboard only AFTER the first project exists.
 */
import React, { useState } from 'react';
import { Mail, Plus, Send, Loader2, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import { usersAPI } from '@/services/api';
import { useI18n } from '@/contexts/I18nContext';
import { usePlan } from '@/contexts/PlanContext';
import PaywallFlow from '@/components/PaywallFlow';
import PlanUpgradeModal, { planLimitFrom } from '@/components/PlanUpgradeModal';

const STORAGE_KEY = 'rektra.inviteTeamCardDismissed';

/** Turn a backend detail into a sentence the owner can act on. */
const explain = (detail, is) => {
  const d = String(detail || '');
  if (/þegar meðlimur|already a member/i.test(d)) {
    return is
      ? 'þetta netfang er þegar með aðgang hjá fyrirtækinu þínu.'
      : 'this email already has access to your company.';
  }
  if (d) return d;
  return is ? 'boðið fór ekki í gegn. Reyndu aftur.' : 'the invitation did not go through. Try again.';
};

const ROLE_OPTIONS = [
  { value: 'worker', is: 'Starfsmaður', en: 'Worker' },
  { value: 'manager', is: 'Verkstjóri', en: 'Manager' },
  { value: 'admin', is: 'Stjórnandi', en: 'Admin' },
  { value: 'bookkeeper', is: 'Bókari', en: 'Bookkeeper' },
];

const InviteTeamCard = () => {
  const { lang } = useI18n();
  const planCtx = usePlan();
  const is = lang === 'is';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [rows, setRows] = useState([{ email: '', role: 'worker' }]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [upgradeLimit, setUpgradeLimit] = useState(null);
  const [upgradeInvite, setUpgradeInvite] = useState(null);
  const [pendingRows, setPendingRows] = useState([]);
  const [upgradePlan, setUpgradePlan] = useState(null);

  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  const update = (idx, patch) =>
    setRows(rs => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const send = async () => {
    const valid = rows.filter(r => r.email.includes('@'));
    if (!valid.length) return;
    setSending(true);
    setError('');
    let ok = 0;
    const failures = [];
    let blocked = null;          // the first row the seat limit stopped
    const notSent = [];
    for (const r of valid) {
      if (blocked) { notSent.push(r); continue; }
      try {
        await usersAPI.invite({ email: r.email.trim(), role: r.role });
        ok += 1;
      } catch (e) {
        const limit = planLimitFrom(e);
        if (limit) { blocked = { limit, row: r }; continue; }
        failures.push({ email: r.email.trim(), detail: e?.response?.data?.detail || '' });
      }
    }
    setSending(false);
    if (ok) {
      toast.success(is ? `${ok} boð send.` : `${ok} invitations sent.`);
      planCtx?.refresh?.();
    }
    if (blocked) {
      // Keep what he typed — the upgrade sends it for him.
      setUpgradeLimit(blocked.limit);
      setUpgradeInvite({ email: blocked.row.email.trim(), role: blocked.row.role });
      setPendingRows(notSent);
    }
    if (failures.length) {
      setError(failures.map(f => `${f.email}: ${explain(f.detail, is)}`).join(' '));
    } else if (ok && !blocked) {
      dismiss();
    }
  };

  return (
    <section
      className="rounded-xl border border-border bg-card p-4"
      data-testid="invite-team-card"
    >
      <div className="flex items-start gap-2.5 mb-3">
        <Users size={16} className="text-foreground-muted mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold">{is ? 'Bjóða teyminu' : 'Invite your team'}</h3>
          <p className="text-xs text-foreground-muted mt-0.5">
            {is
              ? 'Starfsmenn skrá tímana sína sjálfir í símanum.'
              : 'Employees log their own hours from their phone.'}
          </p>
        </div>
        <button
          onClick={dismiss}
          className="p-1 text-foreground-muted hover:text-foreground rounded-md transition-colors flex-shrink-0"
          aria-label={is ? 'Loka' : 'Dismiss'}
          data-testid="invite-team-card-dismiss"
        >
          <X size={15} />
        </button>
      </div>

      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div key={idx} className="flex items-center gap-2" data-testid={`invite-card-row-${idx}`}>
            <div className="flex-1 min-w-0 relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                type="email"
                value={row.email}
                onChange={(e) => update(idx, { email: e.target.value })}
                placeholder={is ? 'netfang' : 'email'}
                className="pl-9 w-full"
                autoComplete="off"
                data-testid={`invite-card-email-${idx}`}
              />
            </div>
            <select
              value={row.role}
              onChange={(e) => update(idx, { role: e.target.value })}
              className="w-40 flex-shrink-0"
              data-testid={`invite-card-role-${idx}`}
            >
              {ROLE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{is ? o.is : o.en}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={send}          disabled={sending || !rows.some(r => r.email.includes('@'))}
          className="btn btn-primary btn-sm flex-1"
          data-testid="invite-team-card-send"
        >
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          {is ? 'Senda boð' : 'Send invitations'}
        </button>
        {rows.length < 4 && (
          <button
            onClick={() => setRows(rs => [...rs, { email: '', role: 'worker' }])}
            className="btn btn-secondary btn-sm"
            data-testid="invite-team-card-add-row"
          >
            <Plus size={15} />
          </button>
        )}
      </div>
      {upgradeLimit && (
        <PlanUpgradeModal
          limit={upgradeLimit}
          invite={upgradeInvite}
          onClose={() => { setUpgradeLimit(null); setUpgradeInvite(null); setPendingRows([]); }}
          onUpgraded={async (res) => {
            setUpgradeLimit(null);
            setUpgradeInvite(null);
            // Whatever was still queued behind the blocked row goes out now.
            for (const r of pendingRows) {
              try { await usersAPI.invite({ email: r.email.trim(), role: r.role }); }
              catch (e) { /* reported on the next attempt */ }
            }
            setPendingRows([]);
            await planCtx?.refresh?.();
            toast.success(is
              ? `Skipt yfir í ${res?.plan_name}. Boðin eru send.`
              : `Switched to ${res?.plan_name}. The invites are on their way.`);
            dismiss();
          }}
        />
      )}

      {error && (
        <p className="mt-3 text-xs text-error" data-testid="invite-team-card-error">
          {error}
        </p>
      )}

      <PaywallFlow
        open={!!upgradePlan}
        initialPlan={upgradePlan}
        mode="full"
        onClose={() => setUpgradePlan(null)}
        onSuccess={async () => {
          setUpgradePlan(null);
          await planCtx?.refresh?.();
          toast.success(is ? 'Áskrift uppfærð.' : 'Subscription updated.');
        }}
      />
    </section>
  );
};

export default InviteTeamCard;
